// src/components/seo/ExamOverviewPage.tsx
// Overzichtspagina voor alle WFT examens - SEO & GEO geoptimaliseerd
// UPDATED: Gebruikt custom useSEO hook (geen externe dependencies)

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';
import { getActiveExams, ExamConfig } from '../../config/examConfigs';

const ExamOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const activeExams = getActiveExams();

  // Groepeer examens per categorie
  const schadeExams = activeExams.filter(e => e.id.includes('schade'));
  const otherExams = activeExams.filter(e => !e.id.includes('schade'));

  const faqs = [
    {
      question: 'Wat zijn WFT examens?',
      answer: 'WFT examens zijn de officiële vakbekwaamheidsexamens voor financieel adviseurs in Nederland. WFT staat voor Wet op het financieel toezicht. Deze examens zijn verplicht voor iedereen die klanten adviseert over financiële producten zoals verzekeringen, hypotheken en beleggingen.'
    },
    {
      question: 'Welke WFT modules zijn er?',
      answer: 'Er zijn verschillende WFT modules: Basis (verplicht voor iedereen), Schade Particulier, Schade Zakelijk, Hypothecair Krediet, Vermogen, Inkomen, Leven, en Zorgverzekeringen.'
    },
    {
      question: 'Hoeveel kost een WFT examen?',
      answer: 'De kosten voor een WFT examen variëren per module, maar liggen gemiddeld tussen €145 en €195 bij CDFD-erkende exameninstituten.'
    },
    {
      question: 'Wat is de slaaggrens voor WFT examens?',
      answer: 'Voor alle WFT examens geldt een slaaggrens van 68%. Dit betekent dat je minimaal 68% van het maximaal aantal te behalen punten moet scoren.'
    }
  ];

  // SEO Hook
  useSEO({
    title: 'WFT Examens Overzicht | Alle WFT Modules & Oefenvragen | WFT Study Buddy',
    description: 'Compleet overzicht van alle WFT examens: Basis, Schade Particulier, Schade Zakelijk, Hypotheken, Vermogen, Inkomen en meer. Bereid je voor met AI-oefenvragen op basis van officiële CDFD toetstermen.',
    canonicalUrl: '/wft-examens',
    faqs: faqs
  });

  const handleStartFree = () => {
    navigate('/register');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <span className="text-2xl">📚</span>
              <span className="font-bold text-xl bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                WFT Study Buddy
              </span>
            </Link>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-gray-900 transition font-medium">
                Home
              </Link>
              <Link to="/#features" className="text-gray-600 hover:text-gray-900 transition font-medium">
                Features
              </Link>
              <Link to="/#pricing" className="text-gray-600 hover:text-gray-900 transition font-medium">
                Prijzen
              </Link>
              <button
                onClick={handleStartFree}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                Start Gratis
              </button>
            </div>

            <button 
              onClick={handleStartFree}
              className="md:hidden bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg font-semibold text-sm"
            >
              Start Gratis
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <nav className="flex items-center justify-center space-x-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-gray-700">Home</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">WFT Examens</span>
          </nav>

          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-gray-100 mb-6">
            <span className="text-lg">🎓</span>
            <span className="text-sm font-semibold text-gray-700">{activeExams.length} WFT Modules beschikbaar</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6 speakable">
            Alle{' '}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              WFT Examens
            </span>{' '}
            op één plek
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto speakable">
            Bereid je voor op elk WFT examen met AI-gegenereerde oefenvragen op basis van officiële CDFD toetstermen. 
            Kies hieronder je module en start direct met oefenen.
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center space-x-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-xl border border-gray-100">
              <span className="text-2xl">📋</span>
              <span className="font-semibold text-gray-900">{activeExams.length} Modules</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-xl border border-gray-100">
              <span className="text-2xl">🤖</span>
              <span className="font-semibold text-gray-900">AI-Powered</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-xl border border-gray-100">
              <span className="text-2xl">📚</span>
              <span className="font-semibold text-gray-900">CDFD Toetstermen</span>
            </div>
          </div>
        </div>
      </section>

      {/* Exams Grid */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Schade Verzekeringen */}
          <div className="mb-16">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-2xl">
                🛡️
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Schadeverzekeringen</h2>
                <p className="text-gray-600">Particuliere en zakelijke schadeverzekeringen</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {schadeExams.map((exam) => (
                <ExamCard key={exam.id} exam={exam} />
              ))}
            </div>
          </div>

          {/* Andere Modules */}
          <div>
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-2xl">
                📊
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Overige Modules</h2>
                <p className="text-gray-600">Basis, Hypotheken, Vermogen, Inkomen en meer</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherExams.map((exam) => (
                <ExamCard key={exam.id} exam={exam} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 speakable">
              Wat zijn WFT examens?
            </h2>
            
            <div className="prose prose-lg max-w-none text-gray-600 speakable">
              <p>
                <strong>WFT examens</strong> zijn de officiële vakbekwaamheidsexamens voor financieel adviseurs in Nederland. 
                WFT staat voor <em>Wet op het financieel toezicht</em>. Deze wet bepaalt dat iedereen die klanten adviseert 
                over financiële producten, moet beschikken over de juiste diploma's.
              </p>
              
              <p>
                De examens worden afgenomen door het <strong>CDFD</strong> (College Deskundigheid Financiële Dienstverlening) 
                bij erkende exameninstituten door heel Nederland. Elk examen toetst je kennis over specifieke financiële 
                producten en bestaat uit drie onderdelen:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Kennis & Begrip (KB)</strong> - Theoretische kennisvragen (1 punt per vraag)</li>
                <li><strong>Vaardigheden & Competenties (VC)</strong> - Casusvragen waarbij je kennis toepast (2 punten)</li>
                <li><strong>Professioneel Gedrag (PG)</strong> - Vragen over ethisch handelen en dilemma's (2 punten)</li>
              </ul>
              
              <p>
                Voor alle WFT examens geldt een <strong>slaaggrens van 68%</strong>. Met WFT Study Buddy kun je oefenen 
                met vragen die zijn gebaseerd op de officiële CDFD toetstermen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Veelgestelde vragen over WFT examens
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} defaultOpen={index === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Klaar om te beginnen?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Kies je WFT module en start vandaag nog met oefenen. De eerste vragen zijn gratis!
          </p>
          
          <button
            onClick={handleStartFree}
            className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Start Gratis Oefenen →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-2xl">📚</span>
                <span className="font-bold text-xl text-white">WFT Study Buddy</span>
              </div>
              <p className="text-sm">De slimste manier om je voor te bereiden op je WFT examen.</p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">WFT Examens</h4>
              <ul className="space-y-2 text-sm">
                {activeExams.slice(0, 6).map(exam => (
                  <li key={exam.id}>
                    <Link to={`/${exam.slug}`} className="hover:text-white transition">{exam.name}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/#features" className="hover:text-white transition">Features</Link></li>
                <li><Link to="/#pricing" className="hover:text-white transition">Prijzen</Link></li>
                <li><Link to="/register" className="hover:text-white transition">Start Gratis</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
             <li><Link to="/contact" className="hover:text-white transition">Contact</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm">
            <p>© <span suppressHydrationWarning>{new Date().getFullYear()}</span> WFT Study Buddy. Alle rechten voorbehouden.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Exam Card Component
const ExamCard: React.FC<{ exam: ExamConfig }> = ({ exam }) => {
  const navigate = useNavigate();

  return (
    <div 
      className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:border-blue-300 transition-all duration-300 cursor-pointer"
      onClick={() => navigate(`/${exam.slug}`)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform">
            {exam.id === 'schade-particulier' ? '🏠' :
             exam.id === 'schade-zakelijk' ? '🏢' :
             exam.id === 'basis' ? '📋' :
             exam.id === 'hypotheken' ? '🏦' :
             exam.id === 'vermogen' ? '📈' :
             exam.id === 'inkomen' ? '💰' :
             exam.id === 'leven' ? '❤️' :
             exam.id === 'zorgverzekeringen' ? '🏥' : '📚'}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
              {exam.fullName}
            </h3>
            {exam.hasQuestions && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                ✓ Vragen beschikbaar
              </span>
            )}
          </div>
        </div>
      </div>

      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
        {exam.shortDescription}
      </p>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center space-x-4 text-gray-500">
          <span>{exam.specs.totalQuestions} vragen</span>
          <span>{exam.specs.duration} min</span>
          <span>{exam.specs.passingScore}%</span>
        </div>
        <span className="text-blue-600 font-semibold group-hover:translate-x-1 transition-transform">
          Bekijk →
        </span>
      </div>
    </div>
  );
};

// FAQ Item Component
const FAQItem: React.FC<{ question: string; answer: string; defaultOpen?: boolean }> = ({ 
  question, 
  answer, 
  defaultOpen = false 
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors"
      >
        <h3 className="text-lg font-semibold text-gray-900 pr-4 speakable">{question}</h3>
        <svg 
          className={`w-5 h-5 text-gray-500 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="px-6 pb-6">
          <p className="text-gray-600 leading-relaxed speakable">{answer}</p>
        </div>
      )}
    </div>
  );
};

export default ExamOverviewPage;